// Lenis smooth scroll.
//
// Lenis verplaatst de échte scrollpositie van het venster (het zet
// window.scrollTo met behavior 'instant' per frame) en legt géén transform op
// body. Daardoor blijven position: sticky, IntersectionObserver en het
// scroll-event gewoon werken: de scroll-animaties in animations.js (.lava /
// .lavaCont) en de parallax daar hangen aan een IntersectionObserver en aan
// window 'scroll', en die blijven dus vuren.
//
// Twee dingen die hierbij horen en elders staan:
// - src/styles/base/reset.css heeft geen `scroll-behavior: smooth` meer. Die
//   vocht met Lenis (twee animaties over elkaar) en maakte elke sprong een
//   animatie: vanaf scrollY 2000 stond een `scrollTo(0, 0)` na 400 ms nog op 38.
// - De stylesheet van Lenis komt hieronder mee, die is aanbevolen (hij zet
//   `height: auto` op html en body zodra Lenis actief is, en `overflow: clip`
//   zolang de instantie gestopt is).

import Lenis from "lenis";
import "lenis/dist/lenis.css";

// prefers-reduced-motion: dan starten we Lenis niet en scrollt de browser zelf.
// Lenis heeft wel een eigen respectReducedMotion (die zet lerp op 1 en laat de
// instantie draaien), maar niets starten is hier beter: geen extra rAF-lus,
// geen extra klassen op html, en een `#anker`-klik en de hash bij het laden
// blijven de gewone browsersprong. Het is ook praktisch: de verificatie van dit
// project gebeurt in headless Chrome met --force-prefers-reduced-motion, en dan
// meet iedereen de kale, onvertraagde scrollpositie.
// We lezen de voorkeur één keer, bij het laden. Zet iemand de instelling om
// terwijl de pagina open staat, dan geldt dat pas op de volgende pagina: dat is
// bewust, want Lenis halverwege afbreken zou ook de ankerlogica hieronder en de
// klassen op html moeten terugdraaien, en dat is meer kans op stukke staat dan
// het waard is voor een instelling die men eens per jaar aanraakt.
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

if (!reduceMotion.matches) {
  startLenis();
}

// Eén keer nakijken in plaats van bij elke wielstap: `node.matches(':open')`
// gooit een SyntaxError in een browser die de selector niet kent.
const kentOpenKeuzelijst = CSS.supports("selector(select:open)");

function startLenis() {
  const lenis = new Lenis({
    // lerp 0.15 in plaats van de standaard 0.1: dit is een zakelijke site, geen
    // showcase. De demping is exponentieel (per frame een vast percentage van
    // de resterende afstand), dus de keuze is puur hoe lang een wielstap
    // nadraait. Gemeten op /onze-aanpak (1440x900), één wielstap van 100 px op
    // 60 fps, tot binnen 5 px en volledig stil:
    //   0.08 → 598 ms / 1099 ms   0.10 → 483 ms / 865 ms
    //   0.12 → 400 ms /  732 ms   0.15 → 317 ms / 584 ms
    //   0.20 → 233 ms /  433 ms
    // 0.08 en 0.10 zwemmen merkbaar achter het wiel aan, 0.20 legt in het
    // eerste frame al 18 van de 100 px af en leest daardoor bijna als een
    // sprong. 0.15 zit ertussen: 14 px in het eerste frame (dus meteen
    // reactie), ~70 px na 100 ms, en na ruim een derde seconde staat het stil.
    lerp: 0.15,

    // 1 en niet hoger: één wielklik legt dezelfde afstand af als zonder Lenis,
    // alleen uitgesmeerd over die ~320 ms. Een multiplier boven 1 maakt de
    // pagina sneller dan de rest van het systeem en dat voelt onbetrouwbaar.
    wheelMultiplier: 1,

    // Alleen het wiel wordt gedempt. Touch blijft native: syncTouch staat
    // standaard uit en dat houden we zo, want op een telefoon of tablet is de
    // scroll van het toestel zelf al vloeiend en door de compositor gedreven.
    // Die overnemen levert alleen risico op (iOS < 16 is onstabiel met
    // syncTouch) en geen zichtbare winst.
    smoothWheel: true,

    // Eigen rAF-lus is niet nodig: er is geen GSAP-ticker of scroll-rig om mee
    // te synchroniseren, animations.js draait op zijn eigen requestAnimationFrame.
    autoRaf: true,

    // De modal in _components/link is `max-h-[90vh] overflow-y-auto` en het
    // debugpaneel in error.twig is `max-h-75 overflow-y-auto`. Zonder dit zou
    // wielen binnen zo'n paneel de pagina eronder verschuiven. De nette manier
    // is `data-lenis-prevent` op die elementen, maar dat zijn templates en die
    // horen niet bij deze taak, dus laat Lenis het zelf uitzoeken. De kosten
    // blijven beperkt: Lenis bewaart de computed style per node 2 s in cache.
    allowNestedScroll: true,

    // anchors: false, want we vangen ankerklikken zelf op (zie onAnchorClick).
    // De ingebouwde variant roept geen preventDefault, dus dan springt de
    // browser óók naar het doel terwijl Lenis er nog naartoe animeert. En hij
    // laat de hash niet in de URL zetten, waardoor een sectie niet meer te
    // delen of te bookmarken is.
    anchors: false,

    // Klik je naar een andere pagina, dan stopt de inertie meteen. Anders loopt
    // de laatste wielbeweging nog na terwijl het verzoek al onderweg is.
    stopInertiaOnNavigate: true,

    // Een open keuzelijst houdt het wiel zelf.
    //
    // De dropdowns in het contactformulier draaien op `appearance: base-select`
    // (zie blocks/form.css): de optielijst is dan een echt paneel met
    // `max-h-72 overflow-y-auto`. Dat paneel is een pseudo-element,
    // `::picker(select)`, en staat dus niet als knoop in de oudertak van het
    // wielgebeuren. `allowNestedScroll` hierboven zoekt juist naar zo'n knoop,
    // vindt niets, en schuift de pagina onder de open lijst weg in plaats van
    // door de opties te lopen. Gemeten: één wielstap boven een optie verzette
    // de pagina 200 px en de lijst geen millimeter.
    //
    // De select zelf staat wél in die tak (de opties zijn echte kinderen), dus
    // daar grijpen we op in. Alleen wanneer de lijst openstaat: dicht blijft
    // wielen boven het veld gewoon de pagina scrollen, zoals overal.
    //
    // Kent de browser `:open` niet, dan kent hij base-select ook niet en tekent
    // hij de lijst buiten de pagina, waar het wiel nooit bij ons langskomt.
    prevent: (node) =>
      kentOpenKeuzelijst && node.nodeName === "SELECT" && node.matches(":open"),

    // autoToggle laten we uit. Die kijkt naar de computed overflow van html, en
    // het mobiele menu zet `overflow: hidden` inline op body. Voor HTML klimt
    // die waarde wel door naar de viewport (html zelf staat op visible), maar de
    // computed overflow van html verandert niet, dus autoToggle zou hier nooit
    // afgaan. Onderaan doen we dat daarom zelf, en preciezer.
    autoToggle: false,
  });

  // Niet als window.lenis: die naam is van de library zelf, daar zet ze haar
  // versievlaggen in (window.lenis.version). Overschrijven zou dat weggooien.
  window.lenisInstance = lenis;

  // #region Ankers

  // Elk blok kan een id krijgen (de slug van het blok, zie de secties in
  // templates/_blocks/**), en het entry type `anchor` linkt daarnaartoe.
  // Zo'n navigatie-item is een eigen URI die serverside doorverwijst naar
  // `/pad#slug`, dus dat komt binnen als een pagina-load met een hash. Een
  // `#hash`-link in CKE-tekst op dezelfde pagina is een gewone klik. Beide
  // gevallen staan hieronder.

  const ANCHOR_DURATION = 0.8; // seconden, ongeacht de afstand

  function targetFromHash(hash) {
    if (!hash || hash === "#") return null;

    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch {
      // Een kapotte %-escape: dan zoeken we op de ruwe waarde verder.
    }
    if (!id) return null;

    // getElementsByName als terugval voor oude `<a name="...">`-ankers.
    return document.getElementById(id) || document.getElementsByName(id)[0] || null;
  }

  function scrollToAnchor(target, options = {}) {
    lenis.scrollTo(target, {
      // Geen offset: de header is `position: relative` (zie _partials/header.twig)
      // en scrollt gewoon mee weg, dus er is geen balk die het doel bedekt.
      offset: 0,
      onComplete: () => {
        // De browser zet bij een echte ankersprong ook de focus (en daarmee het
        // startpunt voor Tab) op het doel. Dat vervalt zodra we preventDefault
        // doen, dus doen we het zelf. preventScroll, anders zou de focus de
        // positie nog eens verzetten.
        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
        }
        target.focus({ preventScroll: true });
      },
      ...options,
    });
  }

  function onAnchorClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    // Cmd/Ctrl/Shift/Alt: de gebruiker wil een nieuw tabblad of venster.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!link || link.hasAttribute("download")) return;
    if (link.target && link.target !== "_self") return;

    const url = new URL(link.href, window.location.href);
    if (!url.hash) return;

    // Alleen een anker op déze pagina. Wijst de link naar een ander pad, dan
    // moet de browser gewoon navigeren en handelt de hash-bij-laden het af.
    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;
    if (url.search !== window.location.search) return;

    const target = targetFromHash(url.hash);
    if (!target) return; // Doel bestaat niet: laat de browser het proberen.

    event.preventDefault();
    scrollToAnchor(target, { duration: ANCHOR_DURATION });

    // De hash hoort in de URL: anders is de sectie niet te delen en werkt de
    // terugknop niet. pushState en niet location.hash, want dat laatste laat de
    // browser alsnog naar het doel springen bovenop onze animatie.
    if (window.location.hash !== url.hash) {
      history.pushState(null, "", url.hash);
    }
  }

  document.addEventListener("click", onAnchorClick);

  // Binnenkomen op /onze-aanpak#stappen. De browser springt zelf al naar het
  // doel (dat mag, er is geen scroll-behavior: smooth meer), maar de positie
  // klopt daarna niet altijd: beelden die pas daarna hun ruimte innemen
  // verschuiven de pagina onder je weg. Daarom zetten we de positie na het
  // laden nog eens goed, hard (immediate) en niet als animatie: wie op een
  // ankerlink klikt wil op die sectie landen, niet eerst bovenaan beginnen.
  function alignToHash() {
    const target = targetFromHash(window.location.hash);
    if (target) lenis.scrollTo(target, { immediate: true });
  }

  alignToHash(); // Vite-modules zijn deferred, dus de DOM staat er al.

  // Bij 'load' nog een keer, maar niet meer zodra de bezoeker zelf gescrold
  // heeft: op een trage verbinding is dat een reëel scenario en dan zou dit de
  // pagina onder zijn handen terugtrekken. 'virtual-scroll' vuurt alleen bij
  // echte wiel- en touchinvoer, niet bij onze eigen scrollTo.
  let userHasScrolled = false;
  lenis.on("virtual-scroll", () => {
    userHasScrolled = true;
  });

  window.addEventListener("load", () => {
    if (!userHasScrolled) alignToHash();
  });

  // Terug- en vooruitknop tussen twee ankers op dezelfde pagina: dat is een
  // hashchange en geen page load, dus daar moeten we zelf naartoe scrollen.
  window.addEventListener("hashchange", () => {
    const target = targetFromHash(window.location.hash);
    if (target) scrollToAnchor(target, { duration: ANCHOR_DURATION });
  });

  // #endregion Ankers

  // #region Scrollslot

  // Het mobiele menu in _partials/header.twig zet met een x-effect
  // `document.body.style.overflow` op 'hidden' zolang het open staat onder
  // 1280 px, en dat menu is een `fixed inset-0`-overlay over de hele pagina.
  // Dat slot houdt alleen gebruikersinvoer tegen, niet programmatisch
  // scrollen: gemeten op 390x844 met het slot erop landde een
  // `window.scrollTo({ top: 500 })` netjes op 500. En programmatisch scrollen
  // is precies wat Lenis doet: het vangt het wielevent zelf af en zet de
  // positie per frame met window.scrollTo. Zonder deze koppeling schoof de
  // pagina dus gewoon door achter het open menu: 6 wielstappen van 200 px
  // brachten scrollY op 1199 in plaats van 0. Zonder Lenis (zelfde test, kale
  // browser) bleef die 0, dus dit is iets wat Lenis introduceert en hier hoort.
  //
  // We kijken naar de inline overflow van body en html en niet naar de
  // computed waarde, want Lenis zet bij een stop zelf `overflow: clip` op html
  // via zijn stylesheet: op de computed waarde meten zou zichzelf vasthouden.
  // Inline schrijft Lenis niets zolang autoToggle uit staat, dus dit is veilig.
  // Het werkt ook meteen voor andere scrollsloten (Fancybox) die op dezelfde
  // manier de body vastzetten.
  function syncScrollLock() {
    const locked =
      document.body.style.overflow === "hidden" ||
      document.documentElement.style.overflow === "hidden";

    if (locked && !lenis.isStopped) lenis.stop();
    else if (!locked && lenis.isStopped) lenis.start();
  }

  const lockObserver = new MutationObserver(syncScrollLock);
  lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
  lockObserver.observe(document.body, { attributes: true, attributeFilter: ["style"] });
  syncScrollLock();

  // #endregion Scrollslot
}
